import { Injectable } from '@angular/core';

import { DataEntityField } from '@osos/shared/api';
import {
  DataTypeEnum,
  FieldPresenterModel,
  FieldValidationModel,
  LocalizedValueModel,
} from '@osos/shared/types';
import { ParserBase } from '../../bases/parser/parser-base';

@Injectable()
export class FieldParserV1Service extends ParserBase<
  DataEntityField,
  FieldPresenterModel
> {
  constructor() {
    super(DataEntityField, FieldPresenterModel, 1);
  }

  getPresenterModel(model: DataEntityField): FieldPresenterModel {
    const presenter = {
      ...(model as any),
      specificationVersion: this.version,
    } as FieldPresenterModel;
    presenter.id = model.id;
    presenter.name = model.name;
    presenter.description = model.description as LocalizedValueModel;
    presenter.normalizedName = model.normalizedName ?? model.id;
    presenter.dataType = model.dataType;
    presenter.renderOptions = model.renderOptions;
    if (model.dataType === DataTypeEnum.Entity) {
      presenter.sources = model.sources?.map((s) => ({
        ...s,
        cascadeEnabled: !!s?.relatedTo,
      })) ?? [
        {
          entity: model.source,
          entriesSource: model.sourceOptions?.entriesSource,
          cascadeEnabled: !!model.sourceOptions?.relatedTo,
          entriesSourceParameters:
            model.sourceOptions?.entriesSourceParameters ?? [],
          entriesSourceDependencies:
            model.sourceOptions?.entriesSourceDependencies ?? [],
          relatedTo: model.sourceOptions?.relatedTo,
          relation: model.sourceOptions?.relation,
        },
      ];

      if (model.sources && model.sources.length === 1) {
        // Fill source if the sources array length is 1 for background compatibility
        presenter.source = model.sources[0].entity;
      } else {
        presenter.source = null;
      }
    }
    presenter.dataCollection = model.dataCollection;
    presenter.validations = (model.validations as FieldValidationModel) ?? {};
    presenter.valueValidators = model.valueValidators ?? [];

    // @TODO: Remove this parsing after supporting the multi-value config on UI.
    presenter.options = {
      ...model.options,
      multiValue: model.options?.multiValue && {
        min:
          typeof model.options?.multiValue?.min === 'number'
            ? model.options?.multiValue?.min
            : (model.options?.multiValue?.min?.value as number),
        max:
          typeof model.options?.multiValue?.max === 'number'
            ? model.options?.multiValue?.max
            : (model.options?.multiValue?.max?.value as number),
      },
      columnSpan: model.options?.columnSpan,
      isAdvancedDefaultValue:
        model.options?.calculatedValueOptions?.expression?.length > 0,
      autoSelectFirstValue: model.options?.autoSelectFirstValue,
      showAsCategory: model.options?.showAsCategory,
      showAsSubCategory: model.options?.showAsSubCategory,
      showAsRelationField: model.options?.showAsRelationField,

      searchable: model.options?.searchable,
      queryable: model.options?.queryable,

      // For dirty check skip:
      defaultValue: model.options?.defaultValue ?? null,

      repeatable: model.options?.repeatable ?? null,
      hidden: model.options?.hidden ?? { enabled: false, expression: null },
      calculatedValueOptions: model.options?.calculatedValueOptions ?? {
        calculationTriggers: [{ triggerOptions: null, type: 0 }],
        executionScope: 0,
        expression: '',
      },
      autoIncrementOptions: model.options?.autoIncrementOptions ?? null,
      displayOnly: model.options?.displayOnly ?? { enabled: false },
      help: presenter.options?.help,
    };

    presenter.requirements = model.requirements ?? [];

    presenter.locked = {
      enabled: model.locked?.enabled,
      expression: model.locked?.expression,
    };

    presenter._normalizedName = model.normalizedName;

    presenter.sourceOptions = model.sourceOptions
      ? {
          ...model.sourceOptions,
          cascadeEnabled: !!model.sourceOptions?.relatedTo,
          relatedTo: model.sourceOptions?.relatedTo ?? null,
          relation: model.sourceOptions?.relation ?? null,
          entriesSourceParameters:
            model.sourceOptions?.entriesSourceParameters ?? [],
          entriesSourceDependencies:
            model.sourceOptions?.entriesSourceDependencies ?? [],
        }
      : presenter.dataType === DataTypeEnum.Entity
        ? // For dirty check skip:
          {
            cascadeEnabled: false,
            denyCircularRelation: false,
            entriesSource: null,
            relatedTo: null,
            relation: null,
          }
        : null;

    // Assign null to all validation context params
    presenter.validationContext = {
      invalidExpression: null,
      invalidTypeExpression: null,
    };

    // For dirty check skip:
    presenter.block = { internalEntity: null };
    presenter.validations.regex ??= { enabled: false, value: '', flags: '' };

    // For BE use:
    presenter.virtualField = model.virtualField;
    presenter.isImplicit = model.isImplicit;
    presenter.mapping = {
      ...model.mapping,
      searchModeEnabled:
        !!model.mapping?.searchMode && model.mapping?.searchMode > 0,
    };
    // custom validation
    presenter.customValidations = model.customValidations;
    return presenter;
  }
  getDataModel(presenter: FieldPresenterModel): DataEntityField {
    /**
     * Temp solution to save the entity
     */

    const model = {
      specificationVersion: this.version,
      id: presenter.id,
      normalizedName: presenter.normalizedName ?? undefined,
      name: presenter.name ?? undefined,
      description: presenter.description,
      dataType: presenter.dataType,
      options: {
        autoIncrementOptions: presenter.options.autoIncrementOptions,
        hideForEmptyValues: presenter.options.hideForEmptyValues,
        showInTableView: presenter.options.showInTableView,
        showAsTab:
          presenter.options.showAsTab != undefined
            ? presenter.options.showAsTab
            : undefined,
        showAsTitle: presenter.options.showAsTitle,
        showAsSubTitle: presenter.options.showAsSubTitle,
        showAsCaption: presenter.options.showAsCaption,
        showAsMainDate: presenter.options.showAsMainDate,
        showAsMainNumber: presenter.options.showAsMainNumber,
        showAsEntryPicture: presenter.options.showAsEntryPicture,
        showAsCategory: presenter.options?.showAsCategory,
        showAsMainPhone: presenter.options.showAsMainPhone,
        showAsSubCategory: presenter.options?.showAsSubCategory,
        showAsRelationField: presenter.options?.showAsRelationField,
        columnSpan: presenter.options.columnSpan,
        defaultValue:
          presenter.options.isAdvancedDefaultValue ||
          presenter.options.autoSelectFirstValue
            ? undefined
            : presenter.options.defaultValue,
        calculatedValueOptions:
          presenter.options.isAdvancedDefaultValue &&
          !presenter.options.autoSelectFirstValue
            ? presenter.options.calculatedValueOptions
            : undefined,
        autoSelectFirstValue: presenter.options.autoSelectFirstValue,
        // @TODO: This parsing should be removed once the multi value has been supported on UI.
        multiValue:
          presenter.options.multiValue?.min !== null &&
          presenter.options.multiValue?.min !== undefined
            ? {
                min: {
                  value: presenter.options.multiValue?.min,
                  relative: false,
                },
                max: presenter.options.multiValue?.max
                  ? {
                      value: presenter.options.multiValue?.max,
                      relative: false,
                    }
                  : undefined,
              }
            : undefined,
        repeatable:
          presenter.options.repeatable?.min?.value !== null &&
          presenter.options.repeatable?.min?.value !== undefined
            ? {
                displayAs: presenter.options.repeatable?.displayAs
                  ? presenter.options.repeatable.displayAs
                  : undefined,
                min: {
                  value: presenter.options.repeatable?.min?.value,
                  relative: false,
                },
                max: presenter.options.repeatable?.max?.value
                  ? {
                      value: presenter.options.repeatable?.max?.value,
                      relative: false,
                    }
                  : undefined,
              }
            : undefined,
        unique: presenter.options.unique ?? undefined,
        hidden: presenter.options?.hidden,
        readonly: presenter.options?.readonly ?? undefined,
        searchable: presenter.options?.searchable ?? undefined,
        queryable: presenter.options?.queryable ?? undefined,
        displayOnly: presenter.options?.displayOnly ?? { enabled: false },
        help: presenter.options?.help,
      },
      source: presenter.sources?.[0]?.entity,
      sources: presenter.sources
        ?.filter((x) => x.entity)
        ?.map((s) => ({
          entity: s.entity,
          entriesSource: s.entriesSource,
          entriesSourceParameters: s.entriesSourceParameters ?? [],
          entriesSourceDependencies: s.entriesSourceDependencies ?? [],
          relatedTo: s.relatedTo,
          relation: s.relation,
        })),
      ...(!!presenter?.sourceOptions &&
        !!Object.keys(presenter.sourceOptions).length && {
          sourceOptions: {
            entriesSource: presenter.sourceOptions?.entriesSource,
            denyCircularRelation: presenter.sourceOptions?.denyCircularRelation,
            relatedTo: presenter.sourceOptions?.relatedTo,
            relation: presenter.sourceOptions?.relation,
            display: presenter.sourceOptions?.display,
            entriesSourceParameters:
              presenter.sourceOptions?.entriesSourceParameters ?? [],
            entriesSourceDependencies:
              presenter.sourceOptions?.entriesSourceDependencies ?? [],
          },
        }),
      dataCollection: presenter.dataCollection,
      block: presenter.block?.internalEntity
        ? { internalEntity: presenter.block?.internalEntity?.id }
        : undefined,
      renderOptions: presenter.renderOptions,
      validations: {
        ...presenter.validations,
        min: {
          enabled: presenter.validations?.min?.value !== null,
          value: presenter.validations?.min?.value,
          valueSourceType: presenter.validations?.min?.valueSourceType,
        },
        max: {
          enabled: presenter.validations?.max?.value !== null,
          value: presenter.validations?.max?.value,
          valueSourceType: presenter.validations?.max?.valueSourceType,
        },
      },
      requirements: presenter.requirements,
      locked: {
        enabled: presenter.locked?.enabled ?? false,
        expression: presenter.locked?.expression ?? '',
      },
      virtualField: presenter.virtualField,
      isImplicit: presenter.isImplicit,
      valueValidators: presenter.valueValidators ?? [],
      mapping: presenter.mapping?.enabled
        ? {
            referenceFieldId: presenter.mapping.referenceFieldId,
            enabled: presenter.mapping.enabled,
            sourceFieldId: presenter.mapping.sourceFieldId,
            updateMode: presenter.mapping.updateMode,
            searchMode: presenter.mapping.searchModeEnabled
              ? presenter.mapping.searchMode
              : 0,
          }
        : undefined,
      // custome validation
      customValidations: presenter.customValidations ?? [],
    } as DataEntityField;

    return model;
  }
}
